import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isFeaturedSessionId } from '../utils/featuredSession';

// Whether the live session is a featured (authored) case rather than a
// generated one. Derived from the session id, so it survives `partialize`
// without a persisted flag of its own and can never drift out of sync with
// the id the API layer routes on.
export const selectIsFeaturedSession = (state) => isFeaturedSessionId(state?.sessionId);

// Consults the player has left. Mirrors the server gate: the session's own
// limit (easy 5 / medium 3 / hard 1, featured 5), 3 when an old session lacks
// it. Never negative.
export const selectConsultationsRemaining = (state) => Math.max(
  0,
  (state?.consultationLimit || 3) - (state?.consultationsUsed || 0),
);

const initialCipherState = {
  unlocked: false,
  solved: false,
  hintsFound: [],
  attempts: 0,
  rewardRevealed: false,
};

const uniqueHintIds = (hintIds) => (
  Array.isArray(hintIds) ? [...new Set(hintIds.filter(Boolean))] : []
);

export const normalizeCipherState = (cipherState = {}, fallbackState = initialCipherState) => {
  const source = cipherState || {};
  const fallback = fallbackState || initialCipherState;
  const attempts = source.attempts ?? fallback.attempts ?? 0;

  return {
    unlocked: source.unlocked ?? fallback.unlocked ?? false,
    solved: source.solved ?? fallback.solved ?? false,
    hintsFound: uniqueHintIds(
      source.hintsFound ?? source.hints_found ?? fallback.hintsFound ?? fallback.hints_found
    ),
    attempts: Number.isFinite(Number(attempts)) ? Number(attempts) : 0,
    rewardRevealed: (
      source.rewardRevealed ?? source.reward_revealed ?? fallback.rewardRevealed ?? fallback.reward_revealed ?? false
    ),
  };
};

export const DISCOVERY_CATEGORIES = ['suspects', 'clues', 'locations', 'documents'];

/**
 * The single discovery-toast producer.
 *
 * Every player action funnels its genuinely-new items into `newlyDiscovered`
 * through `appendRevealDiff` / `buildSessionRefreshCompletionUpdate` and
 * writes them with `updateGameState` — action, examine, interrogation, cipher
 * and consult alike. Diffing that one write is therefore the single choke
 * point: it cannot miss a path and cannot fire twice for one item.
 *
 * It deliberately does NOT fire on load/refresh/restart hydration, because
 * `loadSavedGame`, `hydrateGeneratedSession`, `startGame` and `resetGame` set
 * state directly instead of going through `updateGameState`.
 *
 * Tutorial writes are excluded so tutorial behaviour is unchanged.
 */
export const buildDiscoveryQueueAdditions = (state = {}, updates = {}) => {
  if (state.isTutorial || updates.isTutorial) return [];

  const incoming = updates.newlyDiscovered;
  if (!incoming || typeof incoming !== 'object') return [];

  const alreadyQueued = new Set(
    (state.animationQueue || []).map((item) => `${item?.type}:${item?.id}`)
  );
  const timestamp = Date.now();
  const additions = [];

  DISCOVERY_CATEGORIES.forEach((type) => {
    const nextIds = Array.isArray(incoming[type]) ? incoming[type] : null;
    if (!nextIds) return;

    const previousIds = new Set(state.newlyDiscovered?.[type] || []);

    nextIds.forEach((id) => {
      if (!id || previousIds.has(id)) return;
      const key = `${type}:${id}`;
      if (alreadyQueued.has(key)) return;
      alreadyQueued.add(key);
      additions.push({ type, id, timestamp });
    });
  });

  return additions;
};

const initialState = {
  selectedTheme: null,
  selectedDifficulty: null,
  caseId: null,
  sessionId: null,
  generationHydrated: false,
  generationHydratedAt: null,
  // Monotonic counter bumped by every hydration door (startGame,
  // hydrateGeneratedSession, loadSavedGame, resetGame). Unlike the wall-clock
  // `generationHydratedAt`, two hydrations inside the same millisecond are
  // still distinguishable; consumers that must react to "the store was
  // re-hydrated" key on this instead of on the clock.
  hydrationNonce: 0,
  // Bumped only when the CONTENT of the case restarts or changes (new case,
  // restart, reset, a different session/case id). A mid-action session
  // refresh re-hydrates the same case and leaves this alone, so transient UI
  // tied to the current playthrough survives it.
  caseEpoch: 0,
  currentLocation: 'crime_scene',
  turnCount: 0,
  consultationsUsed: 0,
  consultations: [], // Store consultation history
  consultationLimit: 3, // Default, will be set per difficulty
  maxTurns: 30, // Default, will be set per difficulty
  discovered: {
    suspects: [],
    locations: ['crime_scene'],
    clues: [],
    documents: [],
  },
  hintedClues: [], // Clues mentioned but location not yet examined
  examinedFocuses: {}, // EXAMINE mini-game: tracks used focuses per location (location_id -> [focus_types])
  examinations: {}, // EXAMINE results per location for CaseFile display
  observationHints: [], // Current observation hints from last EXAMINE
  newlyDiscovered: {
    suspects: [],
    locations: [],
    clues: [],
    documents: [],
  },
  newlyHinted: [],
  narrativeText: '',
  messageHistory: [], // Store all messages with actions
  currentImage: null,
  currentImageVantage2: null,
  gameStatus: 'not_started',
  guideCharacter: null,
  suspects: [],
  clues: [],
  locations: [],
  documents: [],
  selectedCharacter: null, // For character viewer
  caseData: null, // Store full case data including victim
  newsArticles: [], // Store newspaper articles that have been shown
  interrogationDialogues: {}, // Store interrogation dialogue history per suspect
  preSelectedAction: null, // Pre-selected action from quick actions (e.g., Interview button on suspect card)
  preSelectedTarget: null, // Pre-selected target for the action
  isTutorial: false, // Whether currently in tutorial mode
  // Cipher system state
  cipherState: initialCipherState,
  cipherPuzzle: null,      // Full cipher puzzle data (from case_data)
  showCipherModal: false,  // Control cipher modal visibility
  // Phase 3: Settings and animations
  settings: {
    tooltipsEnabled: true,
    keyboardShortcutsEnabled: true,
  },
  animationQueue: [], // Items waiting to animate on discovery
  actionProcessing: false, // Mirrored from ActionBar — true while an action is in flight
  needsSessionRefresh: false,
};

export const useGameStore = create(persist(
  (set) => ({
    ...initialState,
    
    setTheme: (theme) => set({ selectedTheme: theme }),
    
    setDifficulty: (difficulty) => set({ selectedDifficulty: difficulty }),
    
    startGame: () => set((state) => ({
      hydrationNonce: (state.hydrationNonce || 0) + 1,
      caseEpoch: (state.caseEpoch || 0) + 1,
      gameStatus: 'in_progress',
      // Clear case-specific data to ensure fresh initialization
      narrativeText: '',
      messageHistory: [],
      caseId: null,
      sessionId: null,
      generationHydrated: false,
      generationHydratedAt: null,
      currentImage: null,
      currentImageVantage2: null,
      suspects: [],
      clues: [],
      locations: [],
      documents: [],
      guideCharacter: null,
      caseData: null,
      selectedCharacter: null,
      currentLocation: 'crime_scene',
      turnCount: 0,
      consultationsUsed: 0,
      consultations: [],
      consultationLimit: 3,
      maxTurns: 30,
      discovered: {
        suspects: [],
        locations: ['crime_scene'],
        clues: [],
        documents: [],
      },
      hintedClues: [],
      examinedFocuses: {},
      examinations: {},
      observationHints: [],
      newlyDiscovered: {
        suspects: [],
        locations: [],
        clues: [],
        documents: [],
      },
      newlyHinted: [],
      // Pending discovery toasts belong to the case that produced them; a new
      // case must never inherit them (they would resolve against the new
      // rosters and toast items the player has not discovered).
      animationQueue: [],
      newsArticles: [],
      interrogationDialogues: {},
      isTutorial: false,  // Reset tutorial flag for regular games
      // Reset cipher state
      cipherState: initialCipherState,
      cipherPuzzle: null,
      showCipherModal: false,
      needsSessionRefresh: false,
    })),

    updateGameState: (updates) => set((state) => {
      const additions = buildDiscoveryQueueAdditions(state, updates);
      const next = { ...state, ...updates };
      if (additions.length > 0) {
        next.animationQueue = [...(updates.animationQueue ?? state.animationQueue ?? []), ...additions];
      }
      return next;
    }),

    hydrateGeneratedSession: (payload) => set((state) => {
      const initialSessionState = payload?.initialState || {};
      const discovered = initialSessionState.discovered || {};
      const guideCharacter = payload?.guideCharacter || null;
      const openingNarrative = payload?.narrativeText ?? payload?.openingNarrative ?? '';
      const openingGuideId = guideCharacter?.id || 'guide';
      const initialMessageHistory = openingNarrative
        ? [{
            action: 'opening',
            target: openingGuideId,
            narrative: openingNarrative,
            turnNumber: initialSessionState.turnCount ?? 0,
            timestamp: new Date().toISOString(),
          }]
        : [];

      return {
        gameStatus: 'in_progress',
        selectedTheme: payload?.theme ?? null,
        selectedDifficulty: payload?.difficulty ?? null,
        caseId: payload?.caseId ?? null,
        sessionId: payload?.sessionId ?? null,
        caseData: payload?.caseData || (payload?.victim ? { victim: payload.victim } : null),
        narrativeText: openingNarrative,
        guideCharacter,
        currentLocation: payload?.currentLocation ?? initialSessionState.currentLocation ?? 'crime_scene',
        currentImage: payload?.openingImage ?? null,
        currentImageVantage2: payload?.openingImageVantage2 ?? null,
        turnCount: initialSessionState.turnCount ?? 0,
        consultationsUsed: initialSessionState.consultationsUsed ?? 0,
        consultations: initialSessionState.consultations || [],
        consultationLimit: initialSessionState.consultationLimit || 3,
        maxTurns: initialSessionState.maxTurns || 30,
        suspects: initialSessionState.suspects || [],
        clues: initialSessionState.clues || [],
        locations: initialSessionState.locations || [],
        documents: initialSessionState.documents || [],
        discovered: {
          suspects: discovered.suspects || [],
          locations: discovered.locations || ['crime_scene'],
          clues: discovered.clues || [],
          documents: discovered.documents || [],
        },
        hintedClues: initialSessionState.hintedClues || [],
        examinedFocuses: initialSessionState.examinedFocuses || {},
        examinations: initialSessionState.examinations || {},
        observationHints: initialSessionState.observationHints || [],
        messageHistory: Array.isArray(payload?.messageHistory) ? payload.messageHistory : initialMessageHistory,
        newlyDiscovered: {
          suspects: [],
          locations: [],
          clues: [],
          documents: [],
        },
        newlyHinted: [],
        // Hydration is not a discovery: drop anything the previous case left
        // queued so it cannot toast against this case's rosters.
        animationQueue: [],
        selectedCharacter: guideCharacter ? { ...guideCharacter, type: 'guide' } : null,
        newsArticles: initialSessionState.newsArticles || [],
        interrogationDialogues: initialSessionState.interrogationDialogues || {},
        generationHydrated: true,
        generationHydratedAt: Date.now(),
        hydrationNonce: (state.hydrationNonce || 0) + 1,
        caseEpoch: (state.caseEpoch || 0) + 1,
      };
    }),

    addNewsArticle: (article) => set((state) => ({
      newsArticles: [...state.newsArticles, { ...article, viewedAt: new Date().toISOString() }]
    })),
    
    setSelectedCharacter: (character) => set({ selectedCharacter: character }),
    
    addDiscoveredItem: (category, itemId) => set((state) => ({
      discovered: {
        ...state.discovered,
        [category]: state.discovered[category]?.includes(itemId)
          ? state.discovered[category]
          : [...(state.discovered[category] || []), itemId],
      },
    })),
    
    endGame: (status) => set({ gameStatus: status }),
    
    resetGame: () => set((state) => ({
      ...initialState,
      hydrationNonce: (state.hydrationNonce || 0) + 1,
      caseEpoch: (state.caseEpoch || 0) + 1,
    })),
    
    setNarrativeText: (text) => set({ narrativeText: text }),
    
    incrementTurn: () => set((state) => ({ turnCount: state.turnCount + 1 })),
    
    useConsultation: () => set((state) => ({ consultationsUsed: state.consultationsUsed + 1 })),
    
    setSessionId: (sessionId) => set({ sessionId: sessionId }),
    
    addMessage: (action, target, narrative, revealedInfo = null) => set((state) => ({
      messageHistory: [...state.messageHistory, {
        action,
        target,
        narrative,
        turnNumber: state.turnCount,
        timestamp: new Date().toISOString(),
        ...(revealedInfo && { revealedInfo })
      }]
    })),

    markAsViewed: (category, itemId) => set((state) => ({
      newlyDiscovered: {
        ...state.newlyDiscovered,
        [category]: state.newlyDiscovered[category].filter(id => id !== itemId),
      },
    })),

    clearNewlyDiscovered: (category) => set((state) => ({
      newlyDiscovered: {
        ...state.newlyDiscovered,
        [category]: [],
      },
    })),

    markHintedAsViewed: (clueId) => set((state) => ({
      newlyHinted: state.newlyHinted.filter(id => id !== clueId),
    })),

    setPreSelectedAction: (action, target) => set({
      preSelectedAction: action,
      preSelectedTarget: target,
    }),

    // Phase 3: Settings actions
    toggleTooltips: () => set((state) => ({
      settings: {
        ...state.settings,
        tooltipsEnabled: !state.settings.tooltipsEnabled
      }
    })),

    toggleKeyboardShortcuts: () => set((state) => ({
      settings: {
        ...state.settings,
        keyboardShortcutsEnabled: !state.settings.keyboardShortcutsEnabled
      }
    })),

    setSetting: (key, value) => set((state) => ({
      settings: {
        ...state.settings,
        [key]: value
      }
    })),

    // Phase 3: Animation queue actions
    // Kept for explicit producers (CipherModal's reward clue). Deduplicated by
    // type+id so an item the shared producer already queued is never toasted
    // twice.
    addToAnimationQueue: (type, id) => set((state) => (
      (state.animationQueue || []).some((item) => item.type === type && item.id === id)
        ? state
        : { animationQueue: [...(state.animationQueue || []), { type, id, timestamp: Date.now() }] }
    )),

    // Type-aware drain used by DiscoveryNotification once a toast has been
    // shown and dismissed, so the queue is not append-only.
    dropFromAnimationQueue: (type, id) => set((state) => ({
      animationQueue: (state.animationQueue || []).filter(
        item => !(item.type === type && item.id === id)
      ),
    })),

    clearAnimationQueue: () => set({ animationQueue: [] }),

    // Cipher system actions
    setCipherPuzzle: (puzzle) => set({ cipherPuzzle: puzzle }),

    updateCipherState: (updates) => set((state) => ({
      cipherState: normalizeCipherState(updates, state.cipherState),
    })),

    unlockCipher: () => set((state) => ({
      cipherState: {
        ...state.cipherState,
        unlocked: true
      }
    })),

    addCipherHint: (hintId) => set((state) => ({
      cipherState: {
        ...state.cipherState,
        hintsFound: (state.cipherState.hintsFound || []).includes(hintId)
          ? state.cipherState.hintsFound
          : [...(state.cipherState.hintsFound || []), hintId]
      }
    })),

    solveCipher: () => set((state) => ({
      cipherState: {
        ...state.cipherState,
        solved: true
      }
    })),

    incrementCipherAttempts: () => set((state) => ({
      cipherState: {
        ...state.cipherState,
        attempts: state.cipherState.attempts + 1
      }
    })),

    setShowCipherModal: (show) => set({ showCipherModal: show }),

    // Load a saved game session
    loadSavedGame: (savedData) => set((state) => ({
      selectedTheme: savedData.theme,
      selectedDifficulty: savedData.difficulty,
      caseId: savedData.caseId,
      sessionId: savedData.sessionId,
      generationHydrated: true,
      generationHydratedAt: Date.now(),
      hydrationNonce: (state.hydrationNonce || 0) + 1,
      // A restart replays the same case id in the same session, so it cannot
      // be detected by comparing ids — `restartFeaturedCase` says so
      // explicitly. Everything else that changes session or case is content
      // change too; a same-session same-case reload is a refresh.
      caseEpoch: (state.caseEpoch || 0) + (
        savedData.isRestart
        || savedData.sessionId !== state.sessionId
        || savedData.caseId !== state.caseId
          ? 1
          : 0
      ),
      currentLocation: savedData.gameState.currentLocation,
      turnCount: savedData.gameState.turnCount,
      consultationsUsed: savedData.gameState.consultationsUsed || 0,
      consultations: savedData.gameState.consultations || [],
      consultationLimit: savedData.gameState.consultationLimit || 3,
      maxTurns: savedData.gameState.maxTurns || 30,
      discovered: savedData.gameState.discovered,
      hintedClues: savedData.gameState.hintedClues || [],
      // EXAMINE mini-game state restoration
      examinedFocuses: savedData.gameState.examinedFocuses || {},
      examinations: savedData.gameState.examinations || {},
      observationHints: savedData.gameState.observationHints || [],
      newlyDiscovered: {
        suspects: [],
        locations: [],
        clues: [],
        documents: [],
      },
      newlyHinted: [],
      // Same rule as the other hydration doors: a load / refresh / featured
      // restart starts with an empty toast queue.
      animationQueue: [],
      narrativeText: savedData.messageHistory.length > 0
        ? savedData.messageHistory[savedData.messageHistory.length - 1].narrative
        : '',
      messageHistory: savedData.messageHistory,
      currentImage: savedData.lastImage,
      currentImageVantage2: savedData.lastImageVantage2 || null,
      gameStatus: savedData.gameState.status,
      guideCharacter: savedData.guideCharacter,
      suspects: savedData.gameState.suspects,
      clues: savedData.gameState.clues,
      locations: savedData.gameState.locations,
      documents: savedData.gameState.documents,
      selectedCharacter: null,
      caseData: savedData.caseData,
      // newsArticles come from the server load payload (session.news_history); keep the current list if absent
      newsArticles: savedData.gameState.newsArticles || state.newsArticles || [],
      interrogationDialogues: savedData.gameState.interrogationDialogues || {},
      isTutorial: false,  // Reset tutorial flag when loading saved games
      // Restore cipher state
      cipherState: normalizeCipherState(
        savedData.gameState.cipherState ?? savedData.gameState.cipher_state,
      ),
      cipherPuzzle: savedData.caseData?.cipher_puzzle || null,
      showCipherModal: false,
      needsSessionRefresh: false,
    })),
  }),
  {
    name: 'murder-mystery-game',
    version: 2, // Increment this when making breaking changes to persisted state
    // Only persist essential state - exclude large base64 image data to avoid localStorage quota
    partialize: (state) => ({
      selectedTheme: state.selectedTheme,
      selectedDifficulty: state.selectedDifficulty,
      caseId: state.caseId,
      sessionId: state.sessionId,
      gameStatus: state.gameStatus,
      settings: state.settings,
      // Note: We intentionally exclude suspects, clues, locations, documents, caseData,
      // guideCharacter, currentImage, etc. because they contain large base64 images
      // that exceed localStorage quota (~5-10MB limit). Game state is reloaded from
      // the server when resuming a session.
    }),
    migrate: (persistedState, version) => {
      // Migration from version 0 (no version) to version 1
      // Fix: Reset isTutorial flag that may have been stuck as true
      if (version === 0 || version === 1) {
        return {
          ...persistedState,
          isTutorial: false,
        };
      }
      return persistedState;
    },
  }
));
